import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { finalize } from "rxjs/operators";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { InputTextModule } from "primeng/inputtext";
import { TextareaModule } from "primeng/textarea";
import {
  AutoCompleteCompleteEvent,
  AutoCompleteModule,
  AutoCompleteSelectEvent,
} from "primeng/autocomplete";
import { TooltipModule } from "primeng/tooltip";

import { ClientGroupService } from "../../../../core/services/client-group.service";
import { ClientUserDto } from "../../../../core/models/client-group/client-user.dto";
import { JniToastrService } from "../../../../core/services/jni-toastr.service";

/** Maximum allowed length for the Group Name and Description fields. */
const MAX_FIELD_LENGTH = 256;

/** Characters permitted in a group name (letters, numbers, spaces and common punctuation). */
const GROUP_NAME_PATTERN = /^[a-zA-Z0-9 _\-&()./,'#@+]+$/;

@Component({
  selector: "jni-create-group-modal",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TextareaModule,
    AutoCompleteModule,
    TooltipModule,
  ],
  templateUrl: "./create-group-modal.component.html",
  styleUrls: ["./create-group-modal.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateGroupModalComponent implements OnChanges {
  @Input() visible = false;
  @Input() customerAccount = "";
  @Input() projectId?: string;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() groupCreated = new EventEmitter<void>();

  readonly form = new FormGroup({
    groupName: new FormControl("", {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.maxLength(MAX_FIELD_LENGTH),
        Validators.pattern(GROUP_NAME_PATTERN),
      ],
    }),
    description: new FormControl("", {
      nonNullable: true,
      validators: [Validators.maxLength(MAX_FIELD_LENGTH)],
    }),
    userSearch: new FormControl<string | ClientUserDto>("", {
      nonNullable: true,
    }),
  });
  assignedUsers: string[] = [];
  clientUsers: ClientUserDto[] = [];
  suggestions: ClientUserDto[] = [];
  saving = false;

  constructor(
    private _clientGroupService: ClientGroupService,
    private _toastr: JniToastrService,
    private _destroyRef: DestroyRef,
    private _cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["customerAccount"]) {
      this.loadClientUsers();
    }
    // Reload users each time the modal opens so the list is always fresh
    // and resilient to initial-load failures (e.g. server not yet ready).
    if (changes["visible"] && this.visible) {
      this.loadClientUsers();
    }
  }

  closeModal() {
    this.resetForm();
    this.visibleChange.emit(false);
  }

  onVisibleChange(visible: boolean) {
    if (!visible) {
      this.resetForm();
    }
    this.visibleChange.emit(visible);
  }

  searchUsers(event: AutoCompleteCompleteEvent) {
    const query = (event.query ?? "").trim();
    if (query.length < 2) {
      this.suggestions = [];
      return;
    }

    this._clientGroupService
      .searchClientUsers(this.customerAccount, query)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(users => {
        this.suggestions = users.filter(
          user => !this.assignedUsers.includes(user.email)
        );
        this._cdr.markForCheck();
      });
  }

  onUserSelect(event: AutoCompleteSelectEvent) {
    const user = event.value as ClientUserDto;
    this.addEmail(user.email);
  }

  addUser() {
    const value = this.form.controls.userSearch.value;
    if (typeof value === "string") {
      this.addEmail(value);
    }
  }

  removeUser(email: string) {
    this.assignedUsers = this.assignedUsers.filter(u => u !== email);
  }

  createGroup() {
    if (this.saving) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const { groupName, description } = this.form.getRawValue();
    this.saving = true;
    this.form.disable();
    this._clientGroupService
      .createOrUpdateGroup(this.customerAccount, {
        projectId: this.projectId,
        groupName: groupName.trim(),
        description: description.trim() || null,
        users: this.assignedUsers.map(email => ({ email, isActive: true })),
      })
      .pipe(
        finalize(() => {
          this.saving = false;
          this.form.enable();
          this._cdr.markForCheck();
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this._toastr.success("Group created successfully.");
          this.groupCreated.emit();
          this.resetForm();
        },
        error: (error: unknown) => {
          // Show the backend-provided validation message (e.g. duplicate group name) when present;
          // fall back to a generic message for transport/server failures. The form is left intact
          // so the user's entered details are preserved for another attempt.
          const message =
            error instanceof HttpErrorResponse
              ? "Unable to create group. Please try again."
              : error instanceof Error && error.message
                ? error.message
                : "Unable to create group. Please try again.";
          this._toastr.error(message);
        },
      });
  }

  private addEmail(raw: string) {
    const email = (raw ?? "").trim();
    if (!email) {
      this.form.controls.userSearch.setValue("");
      return;
    }
    if (!this.isValidEmail(email)) {
      this._toastr.warning("Please enter a valid email address.");
      return;
    }
    if (this.assignedUsers.includes(email)) {
      this._toastr.warning("This user is already assigned to the group.");
      this.form.controls.userSearch.setValue("");
      return;
    }

    // Enforce the single-client rule before adding: an email already associated with another client
    // cannot be assigned here. The backend owns the association, so it is the source of truth.
    this._clientGroupService
      .validateUserEmail(this.customerAccount, email)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this.assignedUsers.push(email);
          this.form.controls.userSearch.setValue("");
          this._cdr.markForCheck();
        },
        error: (error: unknown) => {
          const message =
            error instanceof HttpErrorResponse
              ? "Unable to validate the email. Please try again."
              : error instanceof Error && error.message
                ? error.message
                : "This email is already associated with another client.";
          this._toastr.error(message);
        },
      });
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private loadClientUsers() {
    if (!this.customerAccount) return;
    this._clientGroupService
      .getClientUsers(this.customerAccount)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(users => {
        this.clientUsers = users;
        this._cdr.markForCheck();
      });
  }

  private resetForm() {
    this.form.reset({ groupName: "", description: "", userSearch: "" });
    this.assignedUsers = [];
    this.suggestions = [];
  }
}
